import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  goodsRecognitions,
  type GoodsRecognitionListItem,
} from '@/api/goodsWriteoffs';
import { refs } from '@/api/refs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsRecognitionsPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['goods-recognitions'],
    queryFn: () => goodsRecognitions.list({ limit: 200 }),
  });
  const whQ = useQuery({ queryKey: ['warehouses-all'], queryFn: () => refs.warehouses() });

  const whName = useMemo(() => {
    const m = new Map<string, string>();
    whQ.data?.forEach((w) => m.set(w.id, w.name));
    return m;
  }, [whQ.data]);

  const cols = useMemo<ColumnDef<GoodsRecognitionListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: 'Дата', width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'warehouse_id', header: 'Склад',
      sortValue: (r) => whName.get(r.warehouse_id) ?? '',
      cell: (r) => whName.get(r.warehouse_id) ?? '',
    },
    {
      key: 'total_amount', header: 'Сума', width: 140, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => fmt2(r.total_amount),
    },
    {
      key: 'posted', header: 'Проведено', width: 130, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => (r.posted ? '✓ так' : 'ні'),
    },
  ], [whName]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Оприбуткування лишків</h2>
        <button className="primary" onClick={() => navigate('/goods-recognitions/new')}>
          + Новий документ
        </button>
      </div>
      <DataTable<GoodsRecognitionListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/goods-recognitions/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
